import React from 'react';
import { Link } from 'wouter';
import { LectureDownloader } from '@/components/LectureDownloader';
import { 
  ChevronLeft, 
  Lightbulb, 
  AlertTriangle, 
  Info, 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle,
  Clock,
  Share2,
  BookOpen,
  FileText,
  Book,
  Dna,
  BrainCircuit,
  Scale,
  Pill,
  Heart,
  Beaker,
  Activity
} from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from '@/components/ui/badge';
import Slideshow, { SlideContent } from '@/components/lectures/Slideshow';

// Simple KeyTakeaway component
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Clinical Note component
const ClinicalNote = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <AlertTriangle className="text-amber-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-amber-800 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Evidence Box component
const EvidenceBox = ({ title, children }: { title: string, children: React.ReactNode }) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-5 my-6">
      <div className="flex items-center mb-2">
        <Info className="text-gray-600 h-5 w-5 mr-2" />
        <h4 className="font-semibold text-gray-900">{title}</h4>
      </div>
      <div className="text-gray-700 text-sm">
        {children}
      </div>
    </div>
  );
};

// GLP-1 Mechanism Diagram
const GLP1MechanismDiagram = () => (
  <svg width="100%" height="100%" viewBox="0 0 800 400" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f8fafc" />
        <stop offset="100%" stopColor="#f1f5f9" />
      </linearGradient>
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.1"/>
      </filter>
      <linearGradient id="cellGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#e0f2fe" />
        <stop offset="100%" stopColor="#bae6fd" />
      </linearGradient>
      <linearGradient id="receptorGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#3b82f6" />
        <stop offset="100%" stopColor="#2563eb" />
      </linearGradient>
      <linearGradient id="ligandGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#0ea5e9" />
        <stop offset="100%" stopColor="#0284c7" />
      </linearGradient>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
      </marker>
    </defs>
    
    {/* Background */}
    <rect width="800" height="400" fill="url(#bgGradient)" rx="5" ry="5" />
    
    {/* Title */}
    <text x="400" y="40" textAnchor="middle" fontFamily="Arial" fontSize="20" fontWeight="bold" fill="#1e293b">GLP-1 Receptor Agonist Mechanism</text>
    
    {/* Cell Membrane and Structures */}
    <rect x="150" y="100" width="500" height="200" rx="20" ry="20" fill="#f8fafc" stroke="#94a3b8" strokeWidth="2" filter="url(#shadow)" />
    
    {/* GLP-1 Receptor */}
    <path d="M250,100 Q250,80 270,80 L290,80 Q310,80 310,100" fill="none" stroke="#3b82f6" strokeWidth="4" />
    <circle cx="280" cy="80" r="15" fill="url(#receptorGradient)" />
    <text x="280" y="85" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="white" fontWeight="bold">R</text>
    <text x="280" y="65" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#1e293b">GLP-1 Receptor</text>
    
    {/* GLP-1 Ligand */}
    <circle cx="280" cy="40" r="12" fill="url(#ligandGradient)" />
    <text x="280" y="44" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="white" fontWeight="bold">G</text>
    <text x="280" y="25" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#1e293b">GLP-1 RA</text>
    
    {/* Binding Arrow */}
    <line x1="280" y1="52" x2="280" y2="65" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
    
    {/* Downstream Effects - Core Pathways */}
    <circle cx="280" cy="150" r="25" fill="#a5b4fc" stroke="#6366f1" strokeWidth="2" />
    <text x="280" y="154" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="white" fontWeight="bold">cAMP</text>
    
    <line x1="280" y1="175" x2="280" y2="200" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
    
    <circle cx="280" cy="230" r="25" fill="#a5b4fc" stroke="#6366f1" strokeWidth="2" />
    <text x="280" y="234" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="white" fontWeight="bold">PKA</text>
    
    {/* Metabolic Effects */}
    <circle cx="180" cy="230" r="22" fill="#fde68a" stroke="#f59e0b" strokeWidth="2" />
    <text x="180" y="234" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#92400e" fontWeight="bold">Insulin</text>
    
    <circle cx="380" cy="230" r="22" fill="#fca5a5" stroke="#ef4444" strokeWidth="2" />
    <text x="380" y="234" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#7f1d1d" fontWeight="bold">Glucagon</text>
    
    <line x1="255" y1="230" x2="202" y2="230" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
    <line x1="305" y1="230" x2="358" y2="230" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
    
    <path d="M190,208 L210,208" stroke="#16a34a" strokeWidth="2" />
    <polygon points="208,204 216,208 208,212" fill="#16a34a" />
    
    <path d="M370,208 L390,208" stroke="#be123c" strokeWidth="2" />
    <line x1="380" y1="203" x2="380" y2="213" stroke="#be123c" strokeWidth="2" />
    
    {/* Additional Physiological Effects */}
    <circle cx="480" cy="150" r="22" fill="#c7d2fe" stroke="#4f46e5" strokeWidth="2" />
    <text x="480" y="154" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#3730a3" fontWeight="bold">Appetite</text>
    
    <circle cx="480" cy="230" r="22" fill="#c7d2fe" stroke="#4f46e5" strokeWidth="2" />
    <text x="480" y="234" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#3730a3" fontWeight="bold">Gastric</text>
    
    <line x1="305" y1="150" x2="458" y2="150" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
    <line x1="305" y1="230" x2="458" y2="230" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
    
    <path d="M470,128 L490,128" stroke="#be123c" strokeWidth="2" />
    <line x1="480" y1="123" x2="480" y2="133" stroke="#be123c" strokeWidth="2" />
    
    <path d="M470,208 L490,208" stroke="#be123c" strokeWidth="2" />
    <line x1="480" y1="203" x2="480" y2="213" stroke="#be123c" strokeWidth="2" />
    
    {/* Labels */}
    <text x="180" y="260" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#1e293b">↑ Insulin</text>
    <text x="180" y="272" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#1e293b">Secretion</text>
    
    <text x="380" y="260" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#1e293b">↓ Glucagon</text>
    <text x="380" y="272" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#1e293b">Release</text>
    
    <text x="480" y="180" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#1e293b">↓ Appetite</text>
    <text x="480" y="260" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#1e293b">↓ Gastric</text>
    <text x="480" y="272" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#1e293b">Emptying</text>
    
    {/* Legend */}
    <circle cx="650" cy="320" r="8" fill="#a5b4fc" stroke="#6366f1" strokeWidth="1" />
    <text x="665" y="324" textAnchor="start" fontFamily="Arial" fontSize="10" fill="#1e293b">Signaling Molecules</text>
    
    <circle cx="650" cy="340" r="8" fill="#fde68a" stroke="#f59e0b" strokeWidth="1" />
    <text x="665" y="344" textAnchor="start" fontFamily="Arial" fontSize="10" fill="#1e293b">Increased Activity</text>
    
    <circle cx="650" cy="360" r="8" fill="#fca5a5" stroke="#ef4444" strokeWidth="1" />
    <text x="665" y="364" textAnchor="start" fontFamily="Arial" fontSize="10" fill="#1e293b">Decreased Activity</text>
  </svg>
);

// GLP-1 Pathway and Aging Diagram
const GLP1LongevityDiagram = () => (
  <svg width="100%" height="100%" viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bgGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f8fafc" />
        <stop offset="100%" stopColor="#f1f5f9" />
      </linearGradient>
      <filter id="shadow2" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.1"/>
      </filter>
      <marker id="arrowhead2" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
      </marker>
    </defs>
    
    {/* Background */}
    <rect width="800" height="500" fill="url(#bgGradient2)" rx="5" ry="5" />
    
    {/* Title */}
    <text x="400" y="40" textAnchor="middle" fontFamily="Arial" fontSize="20" fontWeight="bold" fill="#1e293b">GLP-1 Effects on Aging Pathways</text>
    
    {/* Central GLP-1 Node */}
    <circle cx="400" cy="120" r="40" fill="#bae6fd" stroke="#0284c7" strokeWidth="3" filter="url(#shadow2)" />
    <text x="400" y="115" textAnchor="middle" fontFamily="Arial" fontSize="16" fontWeight="bold" fill="#0c4a6e">GLP-1</text>
    <text x="400" y="135" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#0c4a6e">Receptor Activation</text>
    
    {/* Pathway Nodes */}
    <circle cx="200" cy="240" r="35" fill="#c7d2fe" stroke="#4f46e5" strokeWidth="2" filter="url(#shadow2)" />
    <text x="200" y="235" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#3730a3">AMPK</text>
    <text x="200" y="250" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#3730a3">Activation</text>
    
    <circle cx="400" cy="240" r="35" fill="#fecdd3" stroke="#be123c" strokeWidth="2" filter="url(#shadow2)" />
    <text x="400" y="235" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#9f1239">mTOR</text>
    <text x="400" y="250" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#9f1239">Inhibition</text>
    
    <circle cx="600" cy="240" r="35" fill="#d8b4fe" stroke="#7e22ce" strokeWidth="2" filter="url(#shadow2)" />
    <text x="600" y="235" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#581c87">SIRT1</text>
    <text x="600" y="250" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#581c87">Activation</text>
    
    {/* Connection Lines */}
    <line x1="370" y1="145" x2="230" y2="215" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    <line x1="400" y1="160" x2="400" y2="200" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    <line x1="430" y1="145" x2="570" y2="215" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    
    {/* Aging Hallmarks */}
    <rect x="100" y="350" width="140" height="70" rx="10" ry="10" fill="#bbf7d0" stroke="#16a34a" strokeWidth="2" filter="url(#shadow2)" />
    <text x="170" y="375" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#166534">Autophagy</text>
    <text x="170" y="395" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#166534">Enhanced</text>
    
    <rect x="250" y="350" width="140" height="70" rx="10" ry="10" fill="#bbf7d0" stroke="#16a34a" strokeWidth="2" filter="url(#shadow2)" />
    <text x="320" y="375" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#166534">Inflammation</text>
    <text x="320" y="395" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#166534">Reduced</text>
    
    <rect x="400" y="350" width="140" height="70" rx="10" ry="10" fill="#bbf7d0" stroke="#16a34a" strokeWidth="2" filter="url(#shadow2)" />
    <text x="470" y="375" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#166534">Mitochondrial</text>
    <text x="470" y="395" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#166534">Function Improved</text>
    
    <rect x="550" y="350" width="140" height="70" rx="10" ry="10" fill="#bbf7d0" stroke="#16a34a" strokeWidth="2" filter="url(#shadow2)" />
    <text x="620" y="375" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#166534">Senescence</text>
    <text x="620" y="395" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#166534">Attenuated</text>
    
    {/* Connection Lines to Hallmarks */}
    <line x1="200" y1="275" x2="170" y2="340" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    <line x1="235" y1="240" x2="320" y2="340" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    <line x1="400" y1="275" x2="400" y2="340" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    <line x1="400" y1="275" x2="470" y2="340" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    <line x1="600" y1="275" x2="620" y2="340" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    
    {/* Legend */}
    <circle cx="110" cy="460" r="8" fill="#bae6fd" stroke="#0284c7" strokeWidth="1" />
    <text x="125" y="464" textAnchor="start" fontFamily="Arial" fontSize="10" fill="#1e293b">GLP-1 Activation</text>
    
    <circle cx="250" cy="460" r="8" fill="#c7d2fe" stroke="#4f46e5" strokeWidth="1" />
    <text x="265" y="464" textAnchor="start" fontFamily="Arial" fontSize="10" fill="#1e293b">Metabolic Sensors</text>
    
    <rect x="380" y="452" width="16" height="16" rx="3" ry="3" fill="#bbf7d0" stroke="#16a34a" strokeWidth="1" />
    <text x="405" y="464" textAnchor="start" fontFamily="Arial" fontSize="10" fill="#1e293b">Aging Hallmarks Affected</text>
  </svg>
);

// Table Component
const DrugComparisonTable = () => (
  <div className="overflow-auto">
    <table className="w-full border-collapse">
      <thead>
        <tr className="bg-gray-100">
          <th className="border border-gray-300 p-3 text-left">Agent</th>
          <th className="border border-gray-300 p-3 text-left">Target Receptors</th>
          <th className="border border-gray-300 p-3 text-left">Half-life</th>
          <th className="border border-gray-300 p-3 text-left">Weight Loss Effect</th>
          <th className="border border-gray-300 p-3 text-left">Key Features</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="border border-gray-300 p-3 font-medium">Semaglutide</td>
          <td className="border border-gray-300 p-3">GLP-1R</td>
          <td className="border border-gray-300 p-3">~7 days</td>
          <td className="border border-gray-300 p-3">15-17%</td>
          <td className="border border-gray-300 p-3">Once-weekly dosing; strong evidence for CV benefit</td>
        </tr>
        <tr className="bg-gray-50">
          <td className="border border-gray-300 p-3 font-medium">Tirzepatide</td>
          <td className="border border-gray-300 p-3">GLP-1R + GIPR</td>
          <td className="border border-gray-300 p-3">~5 days</td>
          <td className="border border-gray-300 p-3">20-22%</td>
          <td className="border border-gray-300 p-3">Dual receptor agonist; superior glycemic control</td>
        </tr>
        <tr>
          <td className="border border-gray-300 p-3 font-medium">Retatrutide</td>
          <td className="border border-gray-300 p-3">GLP-1R + GIPR + GCGR</td>
          <td className="border border-gray-300 p-3">~6 days</td>
          <td className="border border-gray-300 p-3">24-26%</td>
          <td className="border border-gray-300 p-3">Triple agonist; enhanced metabolic effects</td>
        </tr>
        <tr className="bg-gray-50">
          <td className="border border-gray-300 p-3 font-medium">Liraglutide</td>
          <td className="border border-gray-300 p-3">GLP-1R</td>
          <td className="border border-gray-300 p-3">~13 hours</td>
          <td className="border border-gray-300 p-3">5-8%</td>
          <td className="border border-gray-300 p-3">Daily dosing; extensive clinical experience</td>
        </tr>
        <tr>
          <td className="border border-gray-300 p-3 font-medium">Cagrilintide + Semaglutide</td>
          <td className="border border-gray-300 p-3">Amylin + GLP-1R</td>
          <td className="border border-gray-300 p-3">~7 days</td>
          <td className="border border-gray-300 p-3">~25%</td>
          <td className="border border-gray-300 p-3">Combination approach; complementary mechanisms</td>
        </tr>
      </tbody>
    </table>
  </div>
);

// Video Container Component
const VideoSection = () => {
  return (
    <div className="mt-8 border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-100 p-4 border-b flex justify-between items-center">
        <h3 className="font-medium">Lecture Video</h3>
        <span className="text-sm text-gray-500">45:00</span>
      </div>
      <div className="aspect-video bg-gray-100 flex items-center justify-center">
        <div className="w-full max-w-3xl mx-auto">
          <div className="relative" style={{ paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
            <iframe 
              src="https://share.descript.com/embed/ykhAmXh1xsX" 
              className="absolute top-0 left-0 w-full h-full"
              style={{ border: 0 }}
              allowFullScreen
              title="GLP-1 Receptor Agonists Lecture"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create lecture slides content
const lectureSlides: SlideContent[] = [
  // Slide 1: Introduction
  {
    title: "GLP-1 Receptor Agonists in Longevity Medicine (2025 Update)",
    content: (
      <>
        <p className="mb-4">
          Glucagon-like peptide-1 receptor agonists (GLP-1 RAs) have transformed from antidiabetic medications into powerful tools for managing obesity and, increasingly, as potential modulators of the aging process. This lecture explores their mechanisms, interactions with longevity pathways, and clinical applications in age management medicine.
        </p>
        
        <KeyTakeaway>
          <p>GLP-1 receptor agonists intersect with multiple hallmarks of aging, making them unique therapeutic candidates that may address age-related decline through several complementary mechanisms beyond their metabolic effects.</p>
        </KeyTakeaway>
        
        <p className="mt-4">
          Recent evidence (2024-2025) has revealed novel mechanisms by which GLP-1 RAs influence aging biology, including effects on:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex flex-col items-center">
            <BrainCircuit className="h-8 w-8 text-blue-600 mb-2" />
            <h3 className="font-medium text-blue-900 mb-1 text-center">Cellular Proteostasis</h3>
            <p className="text-sm text-center">Autophagy enhancement and protein quality control</p>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 flex flex-col items-center">
            <Dna className="h-8 w-8 text-purple-600 mb-2" />
            <h3 className="font-medium text-purple-900 mb-1 text-center">Mitochondrial Function</h3>
            <p className="text-sm text-center">Improved energy metabolism and reduced oxidative stress</p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-100 flex flex-col items-center">
            <Activity className="h-8 w-8 text-green-600 mb-2" />
            <h3 className="font-medium text-green-900 mb-1 text-center">Inflammatory Signaling</h3>
            <p className="text-sm text-center">Attenuation of chronic inflammation and inflammaging</p>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 2: Key Mechanisms and Pathways - Part 1
  {
    title: "Key Mechanisms and Pathways - Part 1",
    content: (
      <>
        <p className="mb-4">
          GLP-1 receptor agonists mimic the effects of endogenous GLP-1, a gut hormone, by activating GLP-1 receptors throughout the body. This leads to enhanced insulin secretion, suppressed glucagon release, delayed gastric emptying, and reduced appetite.
        </p>
        
        <div className="my-6">
          <GLP1MechanismDiagram />
        </div>
        
        <p className="mt-4">
          Beyond these established metabolic actions, GLP-1 RAs influence critical cellular processes relevant to aging. They activate cAMP-dependent protein kinase A (PKA) and β-arrestin pathways, leading to pleiotropic effects in multiple tissues.
        </p>
        
        <ClinicalNote>
          <p>The wide tissue distribution of GLP-1 receptors explains why these medications provide benefits beyond glycemic control, including cardioprotection, neuroprotection, and enhanced metabolic flexibility—all relevant to age-related decline.</p>
        </ClinicalNote>
      </>
    )
  },
  
  // Slide 3: Key Mechanisms and Pathways - Part 2
  {
    title: "Key Mechanisms and Pathways - Part 2",
    content: (
      <>
        <p className="mb-4">
          Recent research reveals GLP-1 RAs directly engage with multiple longevity-associated cellular processes:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-md">
            <h3 className="font-medium text-blue-900 mb-2">Autophagy and Proteostasis</h3>
            <p>GLP-1 RAs promote <strong>autophagy</strong>, enhancing the clearance of damaged proteins and organelles. In neural tissue, they restore autophagy and reduce apoptotic signaling under toxic stress (e.g., amyloid-β exposure).</p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-md">
            <h3 className="font-medium text-blue-900 mb-2">Mitochondrial Function</h3>
            <p><strong>Mitochondrial biogenesis</strong> is boosted through upregulation of co-factors like PGC-1α. Studies show GLP-1 RAs increase expression of DNA repair enzymes in neurons, reducing reactive oxygen species and DNA damage.</p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-md">
            <h3 className="font-medium text-blue-900 mb-2">Anti-Inflammatory Effects</h3>
            <p>GLP-1 RAs <strong>reduce inflammation</strong> by downregulating pro-inflammatory cytokines like TNF-α, IL-1β, and IL-6 in adipose tissue and vascular cells, contributing to lower systemic inflammation ("inflammaging").</p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-md">
            <h3 className="font-medium text-blue-900 mb-2">Cellular Senescence</h3>
            <p>In adipose tissue, GLP-1 signaling can <strong>attenuate cellular senescence</strong>. Studies in mice on liraglutide showed reduced expression of senescence markers and senescence-associated secretory phenotype (SASP).</p>
          </div>
        </div>
        
        <EvidenceBox title="Research Highlight">
          <p>
            In mouse models of neurodegeneration and aging, GLP-1 RAs decreased microglial activation (the brain's inflammatory cells) and lowered beta-amyloid plaque burden. They also increased hippocampal neurogenesis and synaptic density, suggesting neuroprotective remodeling of aging brain circuits.
          </p>
        </EvidenceBox>
      </>
    )
  },
  
  // Slide 4: Pathway Cross-Talk
  {
    title: "Cross-Talk with Major Longevity Pathways",
    content: (
      <>
        <p className="mb-4">
          GLP-1 RAs intersect with key longevity and metabolic pathways, creating synergistic effects on aging biology:
        </p>
        
        <div className="my-6">
          <GLP1LongevityDiagram />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-md">
            <h3 className="font-medium text-gray-900 mb-2">AMPK/mTOR Axis</h3>
            <p className="text-sm">GLP-1 RAs activate AMPK and inhibit mTORC1, mimicking caloric restriction signals that promote autophagy and cellular maintenance over growth.</p>
          </div>
          
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-md">
            <h3 className="font-medium text-gray-900 mb-2">Sirtuins and NAD+</h3>
            <p className="text-sm">Activation of Sirt1 by GLP-1 RAs promotes stress resistance and metabolic efficiency, potentially amplifying benefits of NAD+ precursors like NMN.</p>
          </div>
          
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-md">
            <h3 className="font-medium text-gray-900 mb-2">Inflammatory Pathways</h3>
            <p className="text-sm">GLP-1 RAs attenuate NF-κB activation and NLRP3 inflammasome activity, reducing chronic inflammation that drives aging.</p>
          </div>
        </div>
        
        <KeyTakeaway>
          <p>The alignment of GLP-1 RA signaling with major longevity pathways suggests these medications may operate as "caloric restriction mimetics" that trigger similar cellular adaptations as dietary restriction without requiring significant calorie reduction.</p>
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 5: Comparing GLP-1 Receptor Agonists
  {
    title: "Comparing GLP-1 Receptor Agonists (2025)",
    content: (
      <>
        <p className="mb-4">
          The GLP-1 RA class has expanded to include not only single-target agents but also dual and triple agonists that engage multiple receptor systems:
        </p>
        
        <DrugComparisonTable />
        
        <EvidenceBox title="Clinical Perspective">
          <p>
            The latest comparative data shows a dose-dependent relationship between receptor engagement complexity and metabolic outcomes: triple agonists (e.g., retatrutide) &gt; dual agonists (e.g., tirzepatide) &gt; single GLP-1 agonists (e.g., semaglutide). However, more complex molecules may also have broader safety considerations.
          </p>
        </EvidenceBox>
      </>
    )
  },
  
  // Slide 6: Synergistic Therapies
  {
    title: "Synergistic Therapies and Combinations",
    content: (
      <>
        <p className="mb-4">
          The mechanistic overlap between GLP-1 RAs and other longevity interventions creates opportunities for synergistic combinations:
        </p>
        
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-6">
          <h3 className="font-bold text-xl text-blue-800 mb-4">Key Synergistic Combinations</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center mb-3">
                <Pill className="h-5 w-5 text-blue-600 mr-3" />
                <h4 className="font-medium text-blue-900">GLP-1 RA + Metformin</h4>
              </div>
              <p className="text-sm text-gray-700">
                <strong>Mechanism:</strong> Complementary AMPK activation and mitochondrial effects
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>Evidence:</strong> Enhanced weight loss and superior glycemic control in combination compared to either agent alone
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>Clinical Pearl:</strong> Consider lower metformin dose (1000mg daily) when combined with GLP-1 RAs to minimize GI side effects
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center mb-3">
                <Pill className="h-5 w-5 text-purple-600 mr-3" />
                <h4 className="font-medium text-purple-900">GLP-1 RA + NAD+ Precursors</h4>
              </div>
              <p className="text-sm text-gray-700">
                <strong>Mechanism:</strong> GLP-1 RA-induced Sirt1 activation increases NAD+ demand
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>Evidence:</strong> Emerging preclinical data showing enhanced mitochondrial function and reduced inflammaging
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>Clinical Pearl:</strong> NMN (500-1000mg) may be more effective than NR when combined with GLP-1 RAs
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center mb-3">
                <Pill className="h-5 w-5 text-amber-600 mr-3" />
                <h4 className="font-medium text-amber-900">GLP-1 RA + Cagrilintide</h4>
              </div>
              <p className="text-sm text-gray-700">
                <strong>Mechanism:</strong> Complementary effects on appetite and gastric emptying through amylin mimicry
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>Evidence:</strong> Clinical trials showing ~25% weight loss, superior to either agent alone
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>Clinical Pearl:</strong> This combination is moving toward FDA approval as a single injection
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center mb-3">
                <Pill className="h-5 w-5 text-green-600 mr-3" />
                <h4 className="font-medium text-green-900">GLP-1 RA + Exercise</h4>
              </div>
              <p className="text-sm text-gray-700">
                <strong>Mechanism:</strong> Complementary effects on mitochondrial biogenesis and muscle protein synthesis
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>Evidence:</strong> Combined approach preserves lean mass while maximizing fat loss
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>Clinical Pearl:</strong> Progressive resistance training 2-3x weekly optimizes this combination
              </p>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          <p>When combining GLP-1 RAs with other interventions, start with 25-50% of the standard GLP-1 RA dose and titrate slowly to minimize overlapping gastrointestinal side effects, particularly for combinations with metformin or acarbose.</p>
        </ClinicalNote>
      </>
    )
  },
  
  // Slide 7: Biomarkers and Monitoring
  {
    title: "Biomarkers and Monitoring",
    content: (
      <>
        <p className="mb-4">
          Comprehensive monitoring of GLP-1 RA therapy in the longevity context extends beyond traditional metabolic markers to include aging-specific biomarkers:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Standard Metabolic Monitoring</h3>
            <div className="space-y-2">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-blue-600 text-xs font-medium">1</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Glycemic Control</p>
                  <p className="text-sm text-gray-700">Fasting glucose, HbA1c, insulin levels, HOMA-IR</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-blue-600 text-xs font-medium">2</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Body Composition</p>
                  <p className="text-sm text-gray-700">DEXA scan for lean/fat mass, visceral fat assessment</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-blue-600 text-xs font-medium">3</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Cardiovascular</p>
                  <p className="text-sm text-gray-700">Lipid panel, blood pressure, carotid IMT</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-blue-600 text-xs font-medium">4</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Safety Monitoring</p>
                  <p className="text-sm text-gray-700">Renal function, pancreatic enzymes, calcitonin</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-indigo-900 mb-3">Longevity-Specific Biomarkers</h3>
            <div className="space-y-2">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">1</span>
                </div>
                <div>
                  <p className="font-medium text-indigo-900">Inflammatory Markers</p>
                  <p className="text-sm text-indigo-700">hsCRP, IL-6, TNF-α, NLR ratio</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">2</span>
                </div>
                <div>
                  <p className="font-medium text-indigo-900">Epigenetic Age</p>
                  <p className="text-sm text-indigo-700">DNA methylation clocks (GrimAge, PhenoAge)</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">3</span>
                </div>
                <div>
                  <p className="font-medium text-indigo-900">Proteomics</p>
                  <p className="text-sm text-indigo-700">Plasma proteomic aging clocks, SomaScan</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">4</span>
                </div>
                <div>
                  <p className="font-medium text-indigo-900">Functional Markers</p>
                  <p className="text-sm text-indigo-700">Grip strength, VO2max, cognitive assessments</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <KeyTakeaway>
          <p>Monitoring should be longitudinal, with baseline measurements prior to therapy initiation and follow-up at 3, 6, and 12 months. The most significant improvements in inflammatory and epigenetic age markers typically require at least 6 months of consistent therapy.</p>
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 8: Dosing Strategies and Patient Selection
  {
    title: "Dosing Strategies and Patient Selection",
    content: (
      <>
        <p className="mb-4">
          Optimizing GLP-1 RA therapy for longevity purposes requires thoughtful dosing strategies and careful patient selection:
        </p>
        
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Optimal Dosing Approach</h3>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="border-b md:border-b-0 md:border-r border-gray-200 pb-4 md:pb-0 md:pr-4">
                <h4 className="font-medium text-gray-900 mb-2">Initiation Phase</h4>
                <p className="text-sm text-gray-700">
                  Start with 25-50% of standard dose. For semaglutide, begin with 0.25mg weekly for 4 weeks.
                </p>
              </div>
              
              <div className="border-b md:border-b-0 md:border-r border-gray-200 pb-4 md:pb-0 md:pr-4">
                <h4 className="font-medium text-gray-900 mb-2">Titration Phase</h4>
                <p className="text-sm text-gray-700">
                  Increase by smallest available increment every 2-4 weeks. Slow titration minimizes side effects.
                </p>
              </div>
              
              <div className="border-b md:border-b-0 md:border-r border-gray-200 pb-4 md:pb-0 md:pr-4">
                <h4 className="font-medium text-gray-900 mb-2">Maintenance Phase</h4>
                <p className="text-sm text-gray-700">
                  Optimal longevity dose may be lower than max approved dose; typically 1.0mg weekly for semaglutide.
                </p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Long-term Strategy</h4>
                <p className="text-sm text-gray-700">
                  Consider stable-dose maintenance with periodic "drug holidays" of 2-4 weeks yearly.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Patient Selection - Who Benefits Most?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
              <h4 className="font-medium text-green-900 mb-2">Ideal Candidates</h4>
              <ul className="space-y-1">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Patients with metabolic dysfunction (insulin resistance, visceral adiposity)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Elevated inflammatory markers (hsCRP &gt; 1.0 mg/L)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Accelerated epigenetic aging (biological age &gt; chronological age)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Family history of neurodegenerative disease</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg border border-red-100">
              <h4 className="font-medium text-red-900 mb-2">Cautionary Patient Types</h4>
              <ul className="space-y-1">
                <li className="flex items-start">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Personal or family history of medullary thyroid carcinoma</span>
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">History of pancreatitis</span>
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Severely underweight patients (BMI &lt; 18.5)</span>
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-sm">Active untreated gastroparesis or severe gastroesophageal reflux</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          <p>For patients specifically seeking longevity benefits without obesity, consider higher frequency/lower dose protocols (e.g., semaglutide 0.25mg twice weekly rather than 1.0mg weekly) to optimize metabolic effects while minimizing weight loss.</p>
        </ClinicalNote>
      </>
    )
  },
  
  // Slide 9: Side Effect Management
  {
    title: "Side Effect Management",
    content: (
      <>
        <p className="mb-4">
          Proactive management of GLP-1 RA side effects maximizes long-term adherence and therapeutic benefits:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-amber-50 px-4 py-2 border-b border-amber-100">
              <h3 className="font-medium text-amber-900">Gastrointestinal Effects</h3>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-700 mb-3">
                <strong>Common issues:</strong> Nausea (30-40%), vomiting (15-25%), diarrhea (10-20%), constipation (10-15%)
              </p>
              <div className="space-y-2">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <p className="text-sm">Slow titration (4-week intervals vs standard 2-week)</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <p className="text-sm">Small, frequent meals (5-6/day vs 3 large meals)</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <p className="text-sm">Ginger supplements (500mg TID) for nausea</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <p className="text-sm">Consider split dosing for daily GLP-1 RAs</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-blue-50 px-4 py-2 border-b border-blue-100">
              <h3 className="font-medium text-blue-900">Nutritional Considerations</h3>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-700 mb-3">
                <strong>Common issues:</strong> Protein malnutrition, micronutrient deficiencies, lean mass loss
              </p>
              <div className="space-y-2">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <p className="text-sm">Protein prioritization (1.2-1.6g/kg/day)</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <p className="text-sm">Comprehensive multivitamin with B-complex</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <p className="text-sm">Resistance training 2-3x weekly</p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <p className="text-sm">Consider leucine-enriched EAA supplementation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-purple-50 px-4 py-2 border-b border-purple-100">
              <h3 className="font-medium text-purple-900">Rare But Serious Concerns</h3>
            </div>
            <div className="p-4">
              <div className="space-y-2">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-red-100 flex items-center justify-center mr-2 mt-0.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Acute Pancreatitis</p>
                    <p className="text-xs text-gray-700">Monitor amylase/lipase at baseline and periodically</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-red-100 flex items-center justify-center mr-2 mt-0.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Gallbladder Disease</p>
                    <p className="text-xs text-gray-700">Higher risk with rapid weight loss; consider ursodiol</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-red-100 flex items-center justify-center mr-2 mt-0.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Retinopathy Progression</p>
                    <p className="text-xs text-gray-700">Annual eye exams for diabetic patients</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-green-50 px-4 py-2 border-b border-green-100">
              <h3 className="font-medium text-green-900">Long-term Management</h3>
            </div>
            <div className="p-4">
              <div className="space-y-2">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Monitor Body Composition</p>
                    <p className="text-xs text-gray-700">DEXA scans every 6-12 months to track lean/fat mass</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Injection Site Rotation</p>
                    <p className="text-xs text-gray-700">Thigh, abdomen, upper arm rotation for weekly injections</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Wean Gradually If Discontinuing</p>
                    <p className="text-xs text-gray-700">Step down doses over 1-2 months to prevent rebound</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 10: Case Studies
  {
    title: "Case Studies: GLP-1 RAs in Longevity Practice",
    content: (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Case 1: Metabolic Optimization</h3>
            
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Patient Profile:</p>
                <p className="text-sm text-gray-600">
                  52-year-old male executive, BMI 28.2, visceral adiposity, HbA1c 5.8%, HOMA-IR 3.2, hsCRP 2.4 mg/L
                </p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-700">Intervention:</p>
                <p className="text-sm text-gray-600">
                  Semaglutide 0.25mg weekly, titrated to 1.0mg over 12 weeks; combined with metformin 500mg BID and NAD+ precursor (NMN 1g daily)
                </p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-700">Outcome at 9 Months:</p>
                <ul className="list-disc text-sm text-gray-600 ml-5 space-y-1">
                  <li>BMI reduced to 24.7 with 76% of weight loss from fat mass</li>
                  <li>HbA1c decreased to 5.2%, HOMA-IR to 1.4</li>
                  <li>hsCRP decreased to 0.8 mg/L</li>
                  <li>Epigenetic age decreased by 2.1 years (GrimAge)</li>
                </ul>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-700">Key Learning:</p>
                <p className="text-sm text-gray-600 italic">
                  "Combination therapy targeting multiple pathways produces synergistic effects on inflammatory markers and biological age measurements."
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Case 2: Neuroprotection Strategy</h3>
            
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Patient Profile:</p>
                <p className="text-sm text-gray-600">
                  61-year-old female, family history of Alzheimer's, normal BMI (23.4), mild cognitive concerns, insulin resistant (fasting insulin 14 μIU/mL)
                </p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-700">Intervention:</p>
                <p className="text-sm text-gray-600">
                  Low-dose semaglutide protocol (0.25mg weekly maintenance) with emphasis on neuroprotection; combined with omega-3 (2g EPA/1g DHA) and Mediterranean diet
                </p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-700">Outcome at 12 Months:</p>
                <ul className="list-disc text-sm text-gray-600 ml-5 space-y-1">
                  <li>Minimal weight change (-3.2 lbs), but improved insulin sensitivity</li>
                  <li>Improved cognitive testing scores (processing speed +14%)</li>
                  <li>Reduced plasma neurofilament light chain levels</li>
                  <li>Improved sleep quality measured by wearable device</li>
                </ul>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-700">Key Learning:</p>
                <p className="text-sm text-gray-600 italic">
                  "Low-dose GLP-1 RA protocols can target neuroprotection with minimal weight loss effects when that's the therapeutic goal."
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Case 3: Inflammaging Intervention</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Patient Profile:</p>
                <p className="text-sm text-gray-600">
                  58-year-old female, post-menopausal, BMI 26.8, elevated inflammatory markers (hsCRP 3.8 mg/L, IL-6 4.2 pg/mL), early sarcopenia, accelerated GrimAge (+5.2 years)
                </p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-700">Intervention:</p>
                <p className="text-sm text-gray-600">
                  Tirzepatide 2.5mg, slowly titrated to 7.5mg weekly over 16 weeks; combined with resistance training, EAA supplementation, and personalized anti-inflammatory diet
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-700">Outcome at 12 Months:</p>
                <ul className="list-disc text-sm text-gray-600 ml-5 space-y-1">
                  <li>BMI reduced to 23.4 with preserved lean mass</li>
                  <li>hsCRP decreased to 0.9 mg/L, IL-6 to 1.8 pg/mL</li>
                  <li>Grip strength increased by 18%</li>
                  <li>GrimAge delta reduced to +2.7 years</li>
                </ul>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-700">Key Learning:</p>
                <p className="text-sm text-gray-600 italic">
                  "Dual-receptor agonism may be particularly effective for addressing inflammaging in patients who also need significant metabolic improvement."
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 11: Future Directions
  {
    title: "Future Directions and Emerging Research",
    content: (
      <>
        <p className="mb-4">
          The field of GLP-1 therapies is evolving rapidly, with several promising developments on the horizon:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <div className="flex items-center mb-3">
              <Beaker className="h-6 w-6 text-blue-700 mr-2" />
              <h3 className="font-medium text-blue-900">Novel Formulations</h3>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-blue-600 text-xs font-medium">1</span>
                </div>
                <span>Oral GLP-1 RAs with improved bioavailability</span>
              </li>
              <li className="flex">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-blue-600 text-xs font-medium">2</span>
                </div>
                <span>Monthly and bi-monthly injection options</span>
              </li>
              <li className="flex">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-blue-600 text-xs font-medium">3</span>
                </div>
                <span>Tissue-specific GLP-1 receptor modulators</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
            <div className="flex items-center mb-3">
              <Scale className="h-6 w-6 text-purple-700 mr-2" />
              <h3 className="font-medium text-purple-900">Combination Therapies</h3>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-purple-600 text-xs font-medium">1</span>
                </div>
                <span>GLP-1 + amylin combinations (e.g., cagrilintide)</span>
              </li>
              <li className="flex">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-purple-600 text-xs font-medium">2</span>
                </div>
                <span>GLP-1 + glucagon + GIP "quad-agonists"</span>
              </li>
              <li className="flex">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-purple-600 text-xs font-medium">3</span>
                </div>
                <span>GLP-1 + leptin receptor modulators</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-100">
            <div className="flex items-center mb-3">
              <Heart className="h-6 w-6 text-green-700 mr-2" />
              <h3 className="font-medium text-green-900">Expanded Applications</h3>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-green-600 text-xs font-medium">1</span>
                </div>
                <span>Neurodegenerative disease prevention trials</span>
              </li>
              <li className="flex">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-green-600 text-xs font-medium">2</span>
                </div>
                <span>Post-cancer metabolic rehabilitation</span>
              </li>
              <li className="flex">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-green-600 text-xs font-medium">3</span>
                </div>
                <span>Sarcopenia-specific protocols with muscle preservation</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Active Research Areas in GLP-1 and Aging</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-start mb-3">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">1</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Lifespan and Healthspan Studies</p>
                  <p className="text-sm text-gray-700">
                    Ongoing studies in mice and non-human primates examining GLP-1 effects on maximum lifespan and age-related disease onset
                  </p>
                </div>
              </div>
              
              <div className="flex items-start mb-3">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">2</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Senolytic Interactions</p>
                  <p className="text-sm text-gray-700">
                    Investigation of sequential or combined GLP-1 RA and senolytic therapy for enhanced removal of senescent cells
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <div className="flex items-start mb-3">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">3</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Epigenetic Modifications</p>
                  <p className="text-sm text-gray-700">
                    Research into how GLP-1 signaling affects DNA methylation patterns and histone modifications across tissues
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                  <span className="text-indigo-600 text-xs font-medium">4</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Microbiome Interactions</p>
                  <p className="text-sm text-gray-700">
                    Exploration of bidirectional relationship between GLP-1 RAs, gut microbiota composition, and short-chain fatty acid production
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <EvidenceBox title="Emerging Evidence to Watch">
          <p>
            Preliminary results from the ELYSIAN trial suggest that chronic semaglutide treatment may modify epigenetic aging signatures in adipose tissue in parallel with metabolic improvements. Full results expected in late 2025 may provide the first direct evidence of GLP-1 RAs' effects on molecular aging in humans.
          </p>
        </EvidenceBox>
      </>
    )
  },
  
  // Slide 12: Clinical Pearls and Recommendations
  {
    title: "Clinical Pearls and Recommendations",
    content: (
      <>
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Evidence-Based Clinical Recommendations</h3>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-600 font-medium">1</span>
              </div>
              <div>
                <p className="font-medium text-blue-900">For Metabolic Optimization</p>
                <p className="text-sm text-gray-700 mt-1">
                  For patients with significant metabolic dysfunction (insulin resistance, visceral adiposity, HbA1c &gt; 5.7%), a GLP-1 RA with strong weight loss effects (semaglutide, tirzepatide) should be considered first-line therapy alongside lifestyle modifications.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-600 font-medium">2</span>
              </div>
              <div>
                <p className="font-medium text-blue-900">For Inflammation Reduction</p>
                <p className="text-sm text-gray-700 mt-1">
                  Patients with elevated inflammatory markers but minimal metabolic dysfunction may benefit from low-dose GLP-1 RA protocols (e.g., semaglutide 0.25-0.5mg weekly) combined with anti-inflammatory diet interventions and targeted supplements.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-600 font-medium">3</span>
              </div>
              <div>
                <p className="font-medium text-blue-900">For Cognitive Protection</p>
                <p className="text-sm text-gray-700 mt-1">
                  Patients with family history of neurodegenerative disease or early cognitive concerns may benefit from GLP-1 RAs that cross the blood-brain barrier effectively (liraglutide, semaglutide) combined with comprehensive brain health protocols.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-blue-600 font-medium">4</span>
              </div>
              <div>
                <p className="font-medium text-blue-900">Drug Selection Hierarchy</p>
                <p className="text-sm text-gray-700 mt-1">
                  Current evidence supports a preference order of: multi-receptor agonists (tirzepatide, retatrutide) &gt; next-generation GLP-1 RAs (semaglutide) &gt; first-generation GLP-1 RAs (liraglutide, exenatide), with selection tailored to patient-specific goals and tolerability.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-amber-50 p-5 rounded-lg border border-amber-200">
            <h3 className="font-semibold text-amber-900 mb-4">Clinical Pearls</h3>
            
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 text-amber-600 mr-2 mt-0.5">
                  <Lightbulb className="h-5 w-5" />
                </div>
                <p className="text-sm text-amber-800">
                  Administer weekly GLP-1 RAs on the same day each week, ideally during the morning hours to minimize sleep disruption from potential nausea.
                </p>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 text-amber-600 mr-2 mt-0.5">
                  <Lightbulb className="h-5 w-5" />
                </div>
                <p className="text-sm text-amber-800">
                  Consider temporary dose reduction or pausing therapy during acute illness with significant GI symptoms to prevent dehydration.
                </p>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 text-amber-600 mr-2 mt-0.5">
                  <Lightbulb className="h-5 w-5" />
                </div>
                <p className="text-sm text-amber-800">
                  For patients who plateau on weight loss or metabolic improvements, consider combination with acarbose (50mg with meals) rather than increasing GLP-1 RA dose above approved ranges.
                </p>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 text-amber-600 mr-2 mt-0.5">
                  <Lightbulb className="h-5 w-5" />
                </div>
                <p className="text-sm text-amber-800">
                  Implementing time-restricted eating (8-10 hour feeding window) can amplify metabolic benefits of GLP-1 RAs through complementary mechanisms.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 p-5 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-900 mb-4">The Horizon Perspective</h3>
            
            <p className="text-sm text-green-800 mb-4">
              GLP-1 receptor agonists represent the first pharmacological interventions that simultaneously address multiple hallmarks of aging, working through nutrient sensing, inflammation modulation, proteostasis enhancement, and metabolic flexibility improvements.
            </p>
            
            <p className="text-sm text-green-800 mb-4">
              Their current widespread adoption for metabolic health offers an unprecedented opportunity to study how modulating these pathways affects biological aging in large human populations with diverse genetic backgrounds.
            </p>
            
            <p className="text-sm text-green-800">
              The next five years will likely bring definitive answers regarding their impact on longevity biomarkers, cognitive trajectories, and perhaps provide the first hints of true healthspan extension from a widely available therapeutic.
            </p>
          </div>
        </div>
      </>
    )
  }
];

const GLP1AgonistsLecture = () => {
  return (
    <>
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb Navigation */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship">Mentorship</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship/lectures">Lectures</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink>GLP-1 Receptor Agonists</BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Back Button */}
        <div className="flex items-center justify-between mb-6">
          <Link href="/mentorship/lectures">
            <Button variant="outline" size="sm" className="gap-1">
              <ChevronLeft className="h-4 w-4" />
              Back to Lectures
            </Button>
          </Link>
          <LectureDownloader lectureName="GLP-1 Agonists" fileName="GLP1Agonists" />
        </div>

        {/* Lecture Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">GLP-1 Receptor Agonists in Longevity Medicine</h1>
          <p className="text-lg text-gray-600 mb-4">
            Molecular mechanisms, clinical applications, and emerging evidence in age management
          </p>
          
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge variant="outline" className="text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100">
              Molecular Medicine
            </Badge>
            <Badge variant="outline" className="text-purple-600 bg-purple-50 border-purple-200 hover:bg-purple-100">
              Pharmacology
            </Badge>
            <Badge variant="outline" className="text-green-600 bg-green-50 border-green-200 hover:bg-green-100">
              Metabolic Health
            </Badge>
            <Badge variant="outline" className="text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100">
              Clinical Protocol
            </Badge>
          </div>
          
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              <span>45 minute read</span>
            </div>
            <div className="flex items-center">
              <BookOpen className="h-4 w-4 mr-1" />
              <span>12 slides</span>
            </div>
            <div className="flex items-center">
              <FileText className="h-4 w-4 mr-1" />
              <span>2025 Update</span>
            </div>
          </div>
        </div>

        {/* Video and Slideshow Section */}
        <div className="grid grid-cols-1 gap-8">
          <div>
            <h2 className="text-xl font-semibold mb-4">Lecture Content</h2>
            
            <Tabs defaultValue="slideshow" className="mb-6">
              <TabsList className="mb-2">
                <TabsTrigger value="slideshow">Slideshow</TabsTrigger>
                <TabsTrigger value="video">Video Lecture</TabsTrigger>
              </TabsList>
              
              <TabsContent value="slideshow">
                <Slideshow slides={lectureSlides} />
              </TabsContent>
              
              <TabsContent value="video">
                <VideoSection />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default GLP1AgonistsLecture;